import { useEffect, useState, type ReactNode } from "react";
import {
  Building2,
  KeyRound,
  Pencil,
  Plus,
  RotateCcw,
  ShieldCheck,
  UserRoundPlus,
  UsersRound,
} from "lucide-react";
import { useForm, useWatch } from "react-hook-form";
import { zodResolver } from "@hookform/resolvers/zod";
import { z } from "zod";
import { useAuth } from "../auth/AuthProvider";
import { Button } from "../../shared/ui/Button";
import { Card, EmptyState, StatCard } from "../../shared/ui/Card";
import { PageSkeleton, StatusBadge } from "../../shared/ui/Status";
import type {
  AccountRecord,
  CreateAssignmentInput,
  CrossStoreAssignment,
  StoreRecord,
  UpdateStaffInput,
} from "./organization.types";
import { useOrganization, useOrganizationActions } from "./useOrganization";
import { MemberCoachAssignmentPanel } from "./MemberCoachAssignmentPanel";

const accountSchema = z.object({
  name: z.string().trim().min(1, "请输入姓名").max(80),
  phone: z.string().regex(/^1\d{10}$/, "请输入正确的11位手机号"),
  role: z.enum(["employee", "manager"]),
  storeId: z.string().uuid("请选择门店"),
  jobTitle: z.string().trim().min(1, "请输入岗位"),
  birthday: z.string().optional().refine(
    (value) => !value || value <= new Date().toISOString().slice(0, 10),
    "生日不能晚于今天",
  ),
});
type AccountForm = z.infer<typeof accountSchema>;

const assignmentSchema = z
  .object({
    employeeId: z.string().uuid("请选择员工"),
    storeId: z.string().uuid("请选择目标门店"),
    memberId: z.string().uuid("请选择本次支援的指定会员"),
    startsAt: z.string().min(1, "请选择开始时间"),
    endsAt: z.string().min(1, "请选择结束时间"),
    reason: z.string().trim().min(2, "请填写授权原因"),
  })
  .refine((value) => new Date(value.endsAt) > new Date(value.startsAt), {
    message: "结束时间必须晚于开始时间",
    path: ["endsAt"],
  });

export function OrganizationPage() {
  const { session, mode } = useAuth();
  const organization = useOrganization();
  const actions = useOrganizationActions();
  const [notice, setNotice] = useState("");
  const [error, setError] = useState("");
  const accountForm = useForm<AccountForm>({
    resolver: zodResolver(accountSchema),
    defaultValues: {
      name: "",
      phone: "",
      role: "employee",
      storeId: "",
      jobTitle: "教练",
      birthday: "",
    },
  });
  const assignmentForm = useForm<CreateAssignmentInput>({
    resolver: zodResolver(assignmentSchema),
    defaultValues: {
      employeeId: "",
      storeId: "",
      memberId: "",
      startsAt: "",
      endsAt: "",
      reason: "",
    },
  });
  const assignmentStoreId = useWatch({
    control: assignmentForm.control,
    name: "storeId",
  });
  useEffect(() => {
    const stores = organization.data?.stores;
    if (!stores?.length) return;
    const first = stores[0].id,
      second = stores[1]?.id ?? first;
    if (!stores.some((s) => s.id === accountForm.getValues("storeId")))
      accountForm.setValue("storeId", first);
    if (!stores.some((s) => s.id === assignmentForm.getValues("storeId")))
      assignmentForm.setValue("storeId", second);
    if (!assignmentForm.getValues("startsAt")) {
      const start = new Date(Date.now() + 86400000);
      start.setMinutes(0, 0, 0);
      const end = new Date(start.getTime() + 8 * 3600000);
      assignmentForm.setValue("startsAt", localDateTime(start));
      assignmentForm.setValue("endsAt", localDateTime(end));
    }
  }, [accountForm, assignmentForm, organization.data?.stores]);
  if (!organization.data || !session) return <PageSkeleton />;
  const data = organization.data;
  const employees = data.accounts.filter((item) => item.role === "employee");
  const assignmentMembers = data.accounts.filter(
    (item) =>
      item.role === "member" &&
      item.status === "active" &&
      item.storeId === assignmentStoreId,
  );
  const run = async (action: () => Promise<unknown>, success: string) => {
    setError("");
    setNotice("");
    try {
      await action();
      setNotice(success);
      return true;
    } catch (cause) {
      setError(cause instanceof Error ? cause.message : "操作失败");
      return false;
    }
  };
  return (
    <div className="page-stack organization-page">
      <header className="page-heading">
        <div>
          <span>总部治理与四店经营</span>
          <h1>组织</h1>
          <p>门店、人员、账号与跨店授权。所有操作保留审计，不物理删除。</p>
        </div>
        <StatusBadge tone="success">
          {mode === "supabase" ? "远程真实数据" : "本地演示数据"}
        </StatusBadge>
      </header>
      {(notice || error) && (
        <div
          className={
            error ? "inline-notice inline-notice--error" : "inline-notice"
          }
        >
          {error || notice}
        </div>
      )}
      <div className="stat-grid">
        <StatCard
          label="直营门店"
          value={`${data.stores.length}`}
          detail="V4确认的武汉四店"
        />
        <StatCard
          label="启用员工/店长"
          value={`${data.accounts.filter((a) => ["employee", "manager"].includes(a.role) && a.status === "active").length}`}
          detail="停用账号不删除历史"
        />
        <StatCard
          label="跨店授权"
          value={`${data.assignments.length}`}
          detail="限定对象与有效期"
        />
      </div>
      <StoreGrid stores={data.stores} accounts={data.accounts} />
      <div className="management-grid">
        <Card className="management-card">
          <div className="card-heading">
            <UserRoundPlus />
            <div>
              <span>仅超管</span>
              <h2>创建员工或店长账号</h2>
            </div>
          </div>
          <form
            onSubmit={accountForm.handleSubmit((input) =>
              run(
                () => actions.createAccount(input),
                "账号已创建并启用，可使用手机号和统一密码88888888登录。",
              ),
            )}
            className="v4-form"
          >
            <Field
              label="姓名"
              error={accountForm.formState.errors.name?.message}
            >
              <input
                {...accountForm.register("name")}
                placeholder="请输入真实姓名"
              />
            </Field>
            <Field
              label="手机号"
              error={accountForm.formState.errors.phone?.message}
            >
              <input
                {...accountForm.register("phone")}
                inputMode="numeric"
                placeholder="一个手机号仅绑定一个身份"
              />
            </Field>
            <Field label="角色">
              <select {...accountForm.register("role")}>
                <option value="employee">员工</option>
                <option value="manager">店长</option>
              </select>
            </Field>
            <Field label="所属门店">
              <select {...accountForm.register("storeId")}>
                {data.stores.map((store) => (
                  <option key={store.id} value={store.id}>
                    {store.name}
                  </option>
                ))}
              </select>
            </Field>
            <Field
              label="岗位"
              error={accountForm.formState.errors.jobTitle?.message}
            >
              <input {...accountForm.register("jobTitle")} />
            </Field>
            <Field
              label="生日（可后补）"
              error={accountForm.formState.errors.birthday?.message}
            >
              <input
                type="date"
                max={new Date().toISOString().slice(0, 10)}
                {...accountForm.register("birthday")}
              />
            </Field>
            <Button disabled={actions.pending}>
              <Plus size={16} />
              创建账号
            </Button>
          </form>
        </Card>
        <Card className="management-card">
          <div className="card-heading">
            <ShieldCheck />
            <div>
              <span>限时最小授权</span>
              <h2>创建跨店支援任务</h2>
            </div>
          </div>
          <form
            onSubmit={assignmentForm.handleSubmit((input) =>
              run(() => actions.createAssignment(input), "跨店授权已创建。"),
            )}
            className="v4-form"
          >
            <Field label="支援员工">
              <select {...assignmentForm.register("employeeId")}>
                <option value="">请选择</option>
                {employees
                  .filter((item) => item.status === "active")
                  .map((item) => (
                    <option key={item.id} value={item.id}>
                      {item.name} · {storeName(data.stores, item.storeId)}
                    </option>
                  ))}
              </select>
            </Field>
            <Field label="目标门店">
              <select {...assignmentForm.register("storeId")}>
                {data.stores.map((store) => (
                  <option key={store.id} value={store.id}>
                    {store.name}
                  </option>
                ))}
              </select>
            </Field>
            <Field
              label="指定会员"
              error={assignmentForm.formState.errors.memberId?.message}
            >
              <select {...assignmentForm.register("memberId")}>
                <option value="">请选择本次任务会员</option>
                {assignmentMembers.map((member) => (
                  <option key={member.id} value={member.id}>
                    {member.name} · {member.phone}
                  </option>
                ))}
              </select>
            </Field>
            <div className="form-split">
              <Field label="开始">
                <input
                  type="datetime-local"
                  {...assignmentForm.register("startsAt")}
                />
              </Field>
              <Field
                label="结束"
                error={assignmentForm.formState.errors.endsAt?.message}
              >
                <input
                  type="datetime-local"
                  {...assignmentForm.register("endsAt")}
                />
              </Field>
            </div>
            <Field label="授权原因">
              <input
                {...assignmentForm.register("reason")}
                placeholder="具体任务或课程"
              />
            </Field>
            <Button disabled={actions.pending || !assignmentMembers.length}>
              <KeyRound size={16} />
              创建授权
            </Button>
            {!assignmentMembers.length && (
              <p className="field-error">
                目标门店暂无当前账号可见的启用会员，不能创建宽泛的整店授权。
              </p>
            )}
          </form>
        </Card>
      </div>
      <AccountDirectory
        accounts={data.accounts}
        stores={data.stores}
        actorRole="super"
        onUpdate={(input) =>
          run(() => actions.updateStaff(input), "人员资料已更新，登录账号同步完成。")
        }
        onDisable={(input) =>
          run(() => actions.disableAccount(input), "账号已停用。")
        }
        onRestore={(id) =>
          run(
            () =>
              actions.restoreAccount({ accountId: id, reason: "总部确认恢复" }),
            "账号已恢复。",
          )
        }
      />
      <div className="section-divider">
        <span>总部可维护全公司</span>
        <h2>会员授课团队</h2>
      </div>
      <MemberCoachAssignmentPanel />
      <AssignmentList
        assignments={data.assignments}
        accounts={data.accounts}
        stores={data.stores}
      />
      {mode === "demo" && (
        <div className="reset-row">
          <Button
            variant="ghost"
            onClick={() => run(actions.reset, "演示组织数据已重置。")}
          >
            <RotateCcw size={15} />
            重置演示数据
          </Button>
        </div>
      )}
    </div>
  );
}

export function AccountDirectory({
  accounts,
  stores,
  actorRole,
  actorStoreId,
  onDisable,
  onRestore,
  onUpdate,
}: {
  accounts: AccountRecord[];
  stores: StoreRecord[];
  actorRole: "manager" | "super";
  actorStoreId?: string | null;
  onDisable(input: {
    accountId: string;
    reason: string;
    handoverTo?: string;
  }): void;
  onRestore(id: string): void;
  onUpdate?(input: UpdateStaffInput): Promise<boolean>;
}) {
  const visible = accounts.filter((item) =>
    actorRole === "super"
      ? item.role !== "member"
      : item.role === "employee" && item.storeId === actorStoreId,
  );
  if (!visible.length)
    return (
      <EmptyState title="暂无人员" description="创建账号后会显示在这里。" />
    );
  return (
    <Card className="directory-card">
      <div className="card-heading">
        <UsersRound />
        <div>
          <span>账号状态与数据范围</span>
          <h2>{actorRole === "super" ? "公司人员目录" : "本店员工目录"}</h2>
        </div>
      </div>
      <div className="directory-table">
        <div className="directory-row directory-row--head">
          <span>人员</span>
          <span>角色/岗位</span>
          <span>门店</span>
          <span>生日</span>
          <span>工作负载</span>
          <span>状态</span>
          <span>操作</span>
        </div>
        {visible.map((item) => (
          <AccountRow
            key={item.id}
            account={item}
            accounts={accounts}
            stores={stores}
            onDisable={onDisable}
            onRestore={onRestore}
            onUpdate={onUpdate}
          />
        ))}
      </div>
    </Card>
  );
}

function AccountRow({
  account,
  accounts,
  stores,
  onDisable,
  onRestore,
  onUpdate,
}: {
  account: AccountRecord;
  accounts: AccountRecord[];
  stores: StoreRecord[];
  onDisable(input: {
    accountId: string;
    reason: string;
    handoverTo?: string;
  }): void;
  onRestore(id: string): void;
  onUpdate?(input: UpdateStaffInput): Promise<boolean>;
}) {
  const [reason, setReason] = useState("");
  const [handoverTo, setHandoverTo] = useState("");
  const [editing, setEditing] = useState(false);
  const [editingProfile, setEditingProfile] = useState(false);
  const [savingProfile, setSavingProfile] = useState(false);
  const [profile, setProfile] = useState<UpdateStaffInput>({
    accountId: account.id,
    name: account.name,
    phone: account.phone,
    role: account.role === "manager" ? "manager" : "employee",
    storeId: account.storeId ?? "",
    jobTitle: account.jobTitle,
    birthday: account.birthday ?? "",
  });
  const toggleProfileEditor = () => {
    if (!editingProfile) {
      setProfile({
        accountId: account.id,
        name: account.name,
        phone: account.phone,
        role: account.role === "manager" ? "manager" : "employee",
        storeId: account.storeId ?? "",
        jobTitle: account.jobTitle,
        birthday: account.birthday ?? "",
      });
    }
    setEditingProfile((value) => !value);
  };
  const receivers = accounts.filter(
    (item) =>
      item.role === "employee" &&
      item.status === "active" &&
      item.storeId === account.storeId &&
      item.id !== account.id,
  );
  return (
    <div className="directory-entry">
    <div className="directory-row">
      <span>
        <strong>{account.name}</strong>
        <small>{account.phone}</small>
      </span>
      <span>
        {roleLabel(account.role)}
        <small>{account.jobTitle}</small>
      </span>
      <span>{storeName(stores, account.storeId)}</span>
      <span>{account.birthday || "未填写"}</span>
      <span>
        {account.assignedMemberCount}名会员
        <small>{account.openTaskCount}项待办</small>
      </span>
      <span>
        <StatusBadge tone={account.status === "active" ? "success" : "danger"}>
          {account.status === "active" ? "启用" : "停用"}
        </StatusBadge>
      </span>
      <span className="row-actions">
        {account.role === "super" ? (
          <small>受保护</small>
        ) : (
          <>
          {onUpdate && (
            <Button variant="ghost" onClick={toggleProfileEditor}>
              <Pencil size={14} />
              编辑
            </Button>
          )}
          {account.status === "active" ? (
          editing ? (
            <div className="inline-action">
              <input
                value={reason}
                onChange={(e) => setReason(e.target.value)}
                placeholder="停用原因"
              />
              {(account.assignedMemberCount > 0 ||
                account.openTaskCount > 0) && (
                <select
                  value={handoverTo}
                  onChange={(e) => setHandoverTo(e.target.value)}
                >
                  <option value="">选择交接人</option>
                  {receivers.map((item) => (
                    <option key={item.id} value={item.id}>
                      {item.name}
                    </option>
                  ))}
                </select>
              )}
              <Button
                variant="danger"
                onClick={() =>
                  onDisable({ accountId: account.id, reason, handoverTo })
                }
              >
                确认停用
              </Button>
              <Button variant="ghost" onClick={() => setEditing(false)}>
                取消
              </Button>
            </div>
          ) : (
            <Button variant="ghost" onClick={() => setEditing(true)}>
              停用
            </Button>
          )
        ) : (
          <Button variant="secondary" onClick={() => onRestore(account.id)}>
            恢复
          </Button>
          )}
          </>
        )}
      </span>
    </div>
    {editingProfile && onUpdate && (
      <form
        className="staff-edit-form"
        onSubmit={async (event) => {
          event.preventDefault();
          setSavingProfile(true);
          const saved = await onUpdate({ ...profile, birthday: profile.birthday || undefined });
          setSavingProfile(false);
          if (saved) setEditingProfile(false);
        }}
      >
        <Field label="姓名">
          <input value={profile.name} onChange={(event) => setProfile({ ...profile, name: event.target.value })} required maxLength={80} />
        </Field>
        <Field label="手机号">
          <input value={profile.phone} onChange={(event) => setProfile({ ...profile, phone: event.target.value })} required inputMode="numeric" pattern="1[0-9]{10}" />
        </Field>
        <Field label="角色">
          <select value={profile.role} onChange={(event) => setProfile({ ...profile, role: event.target.value as "employee" | "manager" })}>
            <option value="employee">员工</option>
            <option value="manager">店长</option>
          </select>
        </Field>
        <Field label="所属门店">
          <select value={profile.storeId} onChange={(event) => setProfile({ ...profile, storeId: event.target.value })}>
            {stores.map((store) => <option key={store.id} value={store.id}>{store.name}</option>)}
          </select>
        </Field>
        <Field label="岗位">
          <input value={profile.jobTitle} onChange={(event) => setProfile({ ...profile, jobTitle: event.target.value })} required maxLength={80} />
        </Field>
        <Field label="生日">
          <input type="date" max={new Date().toISOString().slice(0, 10)} value={profile.birthday || ""} onChange={(event) => setProfile({ ...profile, birthday: event.target.value })} />
        </Field>
        <div className="staff-edit-actions">
          <Button disabled={savingProfile}><Pencil size={14} />{savingProfile ? "保存中…" : "保存修改"}</Button>
          <Button type="button" variant="ghost" onClick={() => setEditingProfile(false)}>收起</Button>
        </div>
      </form>
    )}
    </div>
  );
}

function StoreGrid({
  stores,
  accounts,
}: {
  stores: StoreRecord[];
  accounts: AccountRecord[];
}) {
  return (
    <div className="store-grid">
      {stores.map((store) => (
        <Card key={store.id} className="store-card">
          <Building2 />
          <div>
            <span>{store.code}</span>
            <h2>{store.name}</h2>
            <p>
              {
                accounts.filter(
                  (a) =>
                    a.storeId === store.id &&
                    a.role === "employee" &&
                    a.status === "active",
                ).length
              }
              名员工 ·{" "}
              {
                accounts.filter(
                  (a) =>
                    a.storeId === store.id &&
                    a.role === "member" &&
                    a.status === "active",
                ).length
              }
              名演示会员
            </p>
          </div>
          <StatusBadge tone="success">启用</StatusBadge>
        </Card>
      ))}
    </div>
  );
}
function AssignmentList({
  assignments,
  accounts,
  stores,
}: {
  assignments: CrossStoreAssignment[];
  accounts: AccountRecord[];
  stores: StoreRecord[];
}) {
  return (
    <Card className="directory-card">
      <div className="card-heading">
        <KeyRound />
        <div>
          <span>delegated范围</span>
          <h2>跨店授权记录</h2>
        </div>
      </div>
      {assignments.length ? (
        <div className="assignment-list">
          {assignments.map((item) => (
            <div key={item.id}>
              <strong>
                {accounts.find((a) => a.id === item.employeeId)?.name}
              </strong>
              <span>支援 {storeName(stores, item.storeId)}</span>
              <small>
                {new Date(item.startsAt).toLocaleString("zh-CN")} —{" "}
                {new Date(item.endsAt).toLocaleString("zh-CN")} · {item.reason}
              </small>
            </div>
          ))}
        </div>
      ) : (
        <p className="muted-copy">
          暂无跨店授权。授权不会改变员工主门店，也不会开放目标门店完整会员列表。
        </p>
      )}
    </Card>
  );
}
function Field({
  label,
  error,
  children,
}: {
  label: string;
  error?: string;
  children: ReactNode;
}) {
  return (
    <label className="v4-field">
      <span>{label}</span>
      {children}
      {error && <small>{error}</small>}
    </label>
  );
}
function storeName(stores: StoreRecord[], id: string | null) {
  return stores.find((item) => item.id === id)?.name ?? "总部";
}
function roleLabel(role: AccountRecord["role"]) {
  return { member: "会员", employee: "员工", manager: "店长", super: "超管" }[
    role
  ];
}
function localDateTime(date: Date) {
  const offset = date.getTimezoneOffset() * 60000;
  return new Date(date.getTime() - offset).toISOString().slice(0, 16);
}
